export function sliderMidValue({ min, max, step }) {
  const raw = min + (max - min) * 0.5;
  const stepped = Math.round(raw / step) * step;
  const decimals = (String(step).split(".")[1] || "").length;
  return parseFloat(stepped.toFixed(decimals));
}

export { sliderFillPct } from "./slider-math";

export function formatAdjustableTileValue({ min, max, value, format }) {
  if (format) return format(value);
  if (min === 0 && max === 1) {
    return value.toFixed(2);
  }
  return String(value);
}

import { REVERB_AMOUNT_STEP } from "./reverb-steps";

export function sliderValueFromClientX(clientX, el, { min, max, step }) {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return min;
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const raw = min + ratio * (max - min);
  const stepped = Math.round(raw / step) * step;
  const decimals = (String(step).split(".")[1] || "").length;
  return parseFloat(stepped.toFixed(decimals));
}

const ADJUSTABLE_SLIDER_SPECS = [
  {
    key: "lowPass",
    activeProp: "lowPassMode",
    min: 200,
    max: 3500,
    step: 50,
    valueProp: "lowPassCutoff",
    fallback: 1500,
  },
  {
    key: "highPass",
    activeProp: "highPassMode",
    min: 80,
    max: 3500,
    step: 50,
    valueProp: "highPassCutoff",
    fallback: 500,
  },
  {
    key: "bitcrusher",
    activeProp: "bitcrusherMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "bitcrusherAmount",
    fallback: 0.45,
  },
  {
    key: "bassBoost",
    activeProp: "bassBoostMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "bassBoostAmount",
    fallback: 0.5,
  },
  {
    key: "phaser",
    activeProp: "phaserMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "phaserAmount",
    fallback: 0.5,
  },
  {
    key: "lofiTape",
    activeProp: "lofiTapeMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "lofiHissVolume",
    fallback: 1,
  },
  {
    key: "rainy",
    activeProp: "rainyMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "rainVolume",
    fallback: 0.6,
  },
  {
    key: "coffeeShop",
    activeProp: "coffeeShopMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "coffeeShopVolume",
    fallback: 0.6,
  },
  {
    key: "vinylCrackle",
    activeProp: "vinylCrackleMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "vinylCrackleVolume",
    fallback: 0.55,
  },
  {
    key: "reverb",
    isActive: (props) => (props.reverbAmount ?? 0) > 0,
    min: 0,
    max: 1,
    step: REVERB_AMOUNT_STEP,
    valueProp: "reverbAmount",
    fallback: 0,
  },
  {
    key: "echo",
    activeProp: "echoMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "echoAmount",
    fallback: 0.5,
  },
];

function isAdjustableSliderActive(props, spec) {
  if (spec.isActive) return spec.isActive(props);
  return !!props[spec.activeProp];
}

export function seedSliderTouchedKeys(props) {
  const touched = {};

  ADJUSTABLE_SLIDER_SPECS.forEach(
    ({ key, min, max, step, valueProp, fallback, ...spec }) => {
      if (!isAdjustableSliderActive(props, spec)) return;
      const value =
        typeof props[valueProp] === "number" ? props[valueProp] : fallback;
      const mid = sliderMidValue({ min, max, step });
      if (Math.abs(value - mid) > step / 2) {
        touched[key] = true;
      }
    },
  );

  return touched;
}

export const SHUFFLE_SYNC_PROPS = [
  "slowControl",
  "reverbAmount",
  "videoFx",
  "lowPassMode",
  "highPassMode",
  "bitcrusherMode",
  "bassBoostMode",
  "phaserMode",
  "echoMode",
  "lofiTapeMode",
  "rainyMode",
  "coffeeShopMode",
  "vinylCrackleMode",
  "preservePitchMode",
  "lowPassCutoff",
  "highPassCutoff",
  "bitcrusherAmount",
  "bassBoostAmount",
  "phaserAmount",
  "echoAmount",
  "lofiHissVolume",
  "rainVolume",
  "coffeeShopVolume",
  "vinylCrackleVolume",
];

export function hasShuffleSyncPropsChanged(prevProps, nextProps) {
  return SHUFFLE_SYNC_PROPS.some((prop) => prevProps[prop] !== nextProps[prop]);
}
